import * as React from 'react';
import { t } from '@reos/i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthApiError, confirmPasswordReset } from '../lib/api.js';

const PASSWORD_MISMATCH_MESSAGE = 'Hesla se neshodují';

const confirmSchema = z
  .object({
    newPassword: z.string().min(12, 'Heslo musí mít alespoň 12 znaků'),
    confirmPassword: z.string().min(1, 'Zopakujte nové heslo'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: PASSWORD_MISMATCH_MESSAGE,
    path: ['confirmPassword'],
  });

type ConfirmForm = z.infer<typeof confirmSchema>;

function tokenFromLocation(): { token: string | null; invalid: boolean } {
  const params = new URLSearchParams(window.location.search);
  if (params.get('error') === 'INVALID_TOKEN') return { token: null, invalid: true };
  return { token: params.get('token'), invalid: false };
}

export function ResetPasswordConfirmPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [{ token, invalid: invalidFromUrl }] = React.useState(tokenFromLocation);
  const [tokenInvalid, setTokenInvalid] = React.useState(invalidFromUrl || token === null);
  const [error, setError] = React.useState<string | null>(null);

  const form = useForm<ConfirmForm>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    if (!token) return;
    setError(null);
    try {
      await confirmPasswordReset({ token, newPassword: data.newPassword });
      void navigate({ to: '/login', search: { reset: 'success' } });
    } catch (err) {
      if (err instanceof AuthApiError && (err.code === 'INVALID_TOKEN' || err.code === 'TOKEN_EXPIRED')) {
        setTokenInvalid(true);
        return;
      }
      setError('Nastavení nového hesla se nezdařilo. Zkuste to prosím znovu.');
    }
  });

  if (tokenInvalid) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <h1 className="text-2xl font-bold mb-2">{t('webAdmin.resetPasswordConfirm.expiredTitle', { locale: 'cs' })}</h1>
        <p role="alert" data-testid="reset-confirm-token-invalid" className="text-sm text-red-600 mb-6">
          {t('webAdmin.resetPasswordConfirm.expiredBody', { locale: 'cs' })}
        </p>
        <Link to="/reset-password" data-testid="reset-confirm-request-new" className="underline text-sm">
          {t('webAdmin.resetPasswordConfirm.requestNew', { locale: 'cs' })}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-12">
      <h1 className="text-2xl font-bold mb-2">{t('webAdmin.resetPasswordConfirm.title', { locale: 'cs' })}</h1>
      <p className="text-sm text-neutral-600 mb-6">{t('webAdmin.resetPasswordConfirm.hint', { locale: 'cs' })}</p>
      <form onSubmit={onSubmit} noValidate className="space-y-4" aria-label={t('webAdmin.resetPasswordConfirm.formAriaLabel', { locale: 'cs' })}>
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium mb-1">{t('webAdmin.resetPasswordConfirm.newPassword', { locale: 'cs' })}</label>
          <input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            aria-describedby={form.formState.errors.newPassword ? 'newPassword-error' : undefined}
            {...form.register('newPassword')}
            className="w-full border rounded px-3 py-2"
            data-testid="reset-confirm-password"
          />
          {form.formState.errors.newPassword ? (
            <p id="newPassword-error" role="alert" className="text-sm text-red-600 mt-1">
              {form.formState.errors.newPassword.message}
            </p>
          ) : null}
        </div>
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">{t('webAdmin.resetPasswordConfirm.newPasswordAgain', { locale: 'cs' })}</label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            aria-describedby={form.formState.errors.confirmPassword ? 'confirmPassword-error' : undefined}
            {...form.register('confirmPassword')}
            className="w-full border rounded px-3 py-2"
            data-testid="reset-confirm-password-again"
          />
          {form.formState.errors.confirmPassword ? (
            <p id="confirmPassword-error" role="alert" className="text-sm text-red-600 mt-1">
              {form.formState.errors.confirmPassword.message}
            </p>
          ) : null}
        </div>
        {error ? <p role="alert" className="text-sm text-red-600" data-testid="reset-confirm-error">{error}</p> : null}
        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full bg-neutral-900 text-rh-on-accent rounded px-4 py-2 hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2"
          data-testid="reset-confirm-submit"
        >
          {t('webAdmin.resetPasswordConfirm.submit', { locale: 'cs' })}
        </button>
      </form>
    </div>
  );
}
